import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, X } from 'lucide-react';
import { orderApi } from '../../api/services';
import { ORDER_STATUS, PAYMENT_METHODS, StatusBadge, useOrderHub } from '../../hooks/useOrderHub';

export default function AdminOrders() {
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [cancelModalOrder, setCancelModalOrder] = useState<any | null>(null);
  const [cancelReason, setCancelReason] = useState('Yönetici tarafından iptal edildi');
  const [isCancelling, setIsCancelling] = useState(false);

  const { data, refetch } = useQuery({
    queryKey: ['admin-orders', status, search],
    queryFn: () => orderApi.getAll({ status: status || undefined, search: search || undefined, pageSize: 50 }).then((r) => r.data),
    refetchInterval: 30_000,
  });
  useOrderHub({
    'new-order': () => refetch(),
    'order-updated': () => refetch(),
    'order-cancelled': () => refetch(),
  });

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast.success(ok); refetch(); }
    catch (e: any) { toast.error(e?.response?.data?.error || 'İşlem başarısız'); }
  };

  const handleConfirmCancel = async () => {
    if (!cancelModalOrder) return;
    setIsCancelling(true);
    try {
      await orderApi.cancel(cancelModalOrder.id, cancelReason.trim() || 'Yönetici tarafından iptal edildi');
      toast.success(`${cancelModalOrder.orderNumber} nolu sipariş başarıyla iptal edildi`);
      setCancelModalOrder(null);
      refetch();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'İşlem başarısız');
    } finally {
      setIsCancelling(false);
    }
  };

  const items = data?.items ?? [];
  // Bu sürümde kurye modülü kapalı: 4 (Kurye Bekleniyor) ve 5 (Kurye Atandı) gizli.
  const HIDDEN_STATUSES = [4, 5];
  const visibleStatuses = Object.entries(ORDER_STATUS).filter(([k]) => !HIDDEN_STATUSES.includes(Number(k)));
  const nextStatuses = [2, 3, 6, 7];

  return (
    <div>
      <h1 style={{ marginBottom: 16 }}>Sipariş Yönetimi</h1>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <select className="form-input" style={{ maxWidth: 200 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Tüm durumlar</option>
          {visibleStatuses.map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input className="form-input" style={{ maxWidth: 260 }} placeholder="Sipariş no / ad / telefon" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map((o: any) => {
          const finalized = o.status === 7 || o.status === 8;
          return (
            <div key={o.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <div>
                <p style={{ fontFamily: 'monospace', fontWeight: 700 }}>{o.orderNumber}</p>
                <p style={{ fontSize: '0.88rem', fontWeight: 600 }}>{o.customerName} · {o.customerPhone}</p>
                {o.fullAddress && (
                  <p style={{ fontSize: '0.84rem', color: '#ea580c', fontWeight: 500, margin: '3px 0' }}>
                    📍 {o.fullAddress}
                  </p>
                )}
                {o.addressDescription && (
                  <p className="text-muted" style={{ fontSize: '0.78rem', fontStyle: 'italic', margin: '2px 0 3px' }}>
                    📝 Not: {o.addressDescription}
                  </p>
                )}
                {o.notes && (
                  <p className="text-muted" style={{ fontSize: '0.78rem', fontStyle: 'italic', margin: '2px 0 3px' }}>
                    💬 Sipariş Notu: {o.notes}
                  </p>
                )}
                <p className="text-muted" style={{ fontSize: '0.8rem' }}>
                  {new Date(o.createdAt).toLocaleString('tr-TR')} · {o.itemCount} ürün · {PAYMENT_METHODS[o.paymentMethod]}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontWeight: 700 }}>{Number(o.totalAmount).toFixed(2)} ₺</p>
                <StatusBadge status={o.status} />
              </div>
              {!finalized && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <select className="form-input" value="" onChange={(e) => e.target.value && act(() => orderApi.updateStatus(o.id, { status: Number(e.target.value) }), 'Durum güncellendi')}>
                    <option value="">Durum değiştir</option>
                    {nextStatuses.map((s) => <option key={s} value={s}>{ORDER_STATUS[s].label}</option>)}
                  </select>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => {
                      setCancelModalOrder(o);
                      setCancelReason('Yönetici tarafından iptal edildi');
                    }}
                  >
                    İptal
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && <p className="text-muted text-center" style={{ padding: 48 }}>Sipariş bulunamadı</p>}
      </div>

      {/* ─── Cancel Order Confirmation Modal ─── */}
      {cancelModalOrder && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            className="card"
            style={{
              width: '100%',
              maxWidth: 460,
              borderRadius: 20,
              padding: '28px',
              background: 'white',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AlertTriangle size={22} />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0, color: 'var(--color-text)' }}>
                  Siparişi İptal Et
                </h3>
              </div>
              <button
                onClick={() => setCancelModalOrder(null)}
                className="btn btn-ghost btn-sm"
                style={{ padding: 6, borderRadius: '50%' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Order Details Box */}
            <div
              style={{
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: 12,
                padding: '16px',
                marginBottom: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>Sipariş Kodu:</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '0.95rem', color: 'var(--color-primary)' }}>
                  {cancelModalOrder.orderNumber}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>Sipariş Tutarı:</span>
                <span style={{ fontWeight: 800, fontSize: '1.1rem', color: '#10b981' }}>
                  ₺{Number(cancelModalOrder.totalAmount).toFixed(2)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>Müşteri:</span>
                <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                  {cancelModalOrder.customerName}
                </span>
              </div>
              {cancelModalOrder.fullAddress && (
                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', marginTop: 4, borderTop: '1px solid #eee', paddingTop: 6 }}>
                  📍 {cancelModalOrder.fullAddress}
                </div>
              )}
            </div>

            {/* Prompt Question */}
            <p style={{ fontSize: '0.92rem', color: 'var(--color-text)', marginBottom: 14, fontWeight: 500 }}>
              Bu siparişi iptal etmek istediğinize emin misiniz? İptal işlemi geri alınamaz.
            </p>

            {/* Reason input */}
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label" style={{ fontSize: '0.82rem' }}>İptal Nedeni (Opsiyonel)</label>
              <input
                type="text"
                className="form-input"
                placeholder="Örn: Müşteri arayıp vazgeçti, Stok yetersiz"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => setCancelModalOrder(null)}
                disabled={isCancelling}
                className="btn btn-secondary"
                style={{ flex: 1, borderRadius: 12 }}
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={handleConfirmCancel}
                disabled={isCancelling}
                className="btn btn-danger"
                style={{ flex: 1.5, borderRadius: 12, fontWeight: 700 }}
              >
                {isCancelling ? 'İptal Ediliyor...' : 'Evet, Siparişi İptal Et'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
